import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber } from '../../styles/tokens'
import { Input } from '../../components/FormControls'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'
import CategoryManagement from './CategoryManagement'
import MeasurementPreview from './MeasurementPreview'
import { exportTakeoffReportPdf } from './exportTakeoffReportPdf'
import { exportTakeoffReportExcel } from './exportTakeoffReportExcel'

// Package F (visual redesign) — Professional Construction Takeoff
// Report, matching the approved design reference. STRICTLY
// READ-ONLY regarding quantities: zero measurement/geometry
// mutations, zero new aggregation logic. All quantities come from
// takeoff_item_sheet_breakdown (Package A SSOT); individual
// measurements (Package C) come directly from `measurement` with the
// existing is_active filter. Search/Compact View are pure
// client-side presentation over already-loaded, already-correct data
// — none of them touch the database or recompute anything.

const CATEGORY_COLORS = ['#2563EB', '#EA580C', '#16A34A', '#7C3AED', '#CA8A04']

export default function TakeoffReportPage() {
  const { tenderId, revisionId } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tenderName, setTenderName] = useState(null)
  const [preparedBy, setPreparedBy] = useState(null)
  const [rows, setRows] = useState([])
  const [outOfSyncItemIds, setOutOfSyncItemIds] = useState(new Set())
  const [expandedCategories, setExpandedCategories] = useState(new Set())
  const [expandedItems, setExpandedItems] = useState(new Set())
  const [expandedSheets, setExpandedSheets] = useState(new Set())
  const [measurementsByKey, setMeasurementsByKey] = useState(new Map())
  const [loadingMeasurementKey, setLoadingMeasurementKey] = useState(null)
  const [showCategoryManagement, setShowCategoryManagement] = useState(false)
  const [availableCategories, setAvailableCategories] = useState([]) // for the per-item assignment dropdown
  const [searchQuery, setSearchQuery] = useState('')
  const [compactView, setCompactView] = useState(false)
  // Quantity-first redesign — the Takeoff Summary table is the
  // PRIMARY view (always visible, no expand needed). The existing
  // Category/Item/Sheet/Measurement tree becomes secondary drill-down,
  // shown only when the user explicitly opens it.
  const [activeTab, setActiveTab] = useState('summary') // 'summary' | 'detail' | 'review'
  const [reviewRows, setReviewRows] = useState([])
  const [loadingReview, setLoadingReview] = useState(false)
  const [reviewSelectedItem, setReviewSelectedItem] = useState(null) // { takeoffItemId, drawingVersionId } for Approve/Reject scope
  const [rejectReasonDraft, setRejectReasonDraft] = useState('')
  const [exportingExcel, setExportingExcel] = useState(false)
  // Package D — measurement preview state. previewMeasurement holds
  // the FULL measurement row (needs geometry_data, sheet_id,
  // measurement_type, polarity) plus its resolved drawing_version_id
  // (per the approved data flow: measurement -> sheet_id ->
  // sheet.drawing_version_id — resolved once, at click time, not
  // pre-fetched for every row).
  const [previewMeasurement, setPreviewMeasurement] = useState(null)
  const [previewSheetId, setPreviewSheetId] = useState(null)
  const [previewDrawingVersionId, setPreviewDrawingVersionId] = useState(null)

  async function openMeasurementPreview(measurement, sheetId) {
    const { data: sheet, error: err } = await supabase.from('sheet').select('drawing_version_id').eq('id', sheetId).maybeSingle()
    if (err || !sheet) { setError(err?.message || 'Could not resolve the drawing for this measurement.'); return }
    setPreviewMeasurement(measurement)
    setPreviewSheetId(sheetId)
    setPreviewDrawingVersionId(sheet.drawing_version_id)
  }
  // Detailed Breakdown filter — user picks a Drawing, then optionally
  // a specific Version of it (not necessarily the latest). This is
  // intentionally independent from the Summary's cross-drawing/
  // latest-only scope: the detail tab exists to inspect ANY specific
  // drawing+revision, including superseded ones (e.g. to find
  // measurements accidentally left on an old version).
  const [allDrawings, setAllDrawings] = useState([])
  const [selectedDrawingId, setSelectedDrawingId] = useState(null)
  const [drawingVersionsForSelected, setDrawingVersionsForSelected] = useState([])
  const [selectedDetailVersionId, setSelectedDetailVersionId] = useState(null)
  const [detailRows, setDetailRows] = useState([])
  const [loadingDetail, setLoadingDetail] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function loadContext() {
      const { data: tender } = await supabase.from('tender').select('name').eq('id', tenderId).maybeSingle()
      if (!cancelled && tender) setTenderName(tender.name)
      const { data: authData } = await supabase.auth.getUser()
      if (!cancelled) setPreparedBy(authData?.user?.email?.split('@')[0] || null)
      // All categories for this tender (not just ones currently used
      // by items with active measurements) — needed so the user can
      // assign a brand-new empty category to an item.
      const { data: cats } = await supabase.from('takeoff_category').select('id, name, code').eq('tender_id', tenderId).eq('is_active', true).order('sort_order')
      if (!cancelled) setAvailableCategories(cats || [])
    }
    loadContext()
    return () => { cancelled = true }
  }, [tenderId])

  // Assign a Takeoff Item to a Category — the missing link that lets
  // the Category system actually organize the report. Updates ONLY
  // takeoff_item.takeoff_category_id — never touches measurement,
  // geometry, calibration, or any aggregation/BOQ logic.
  async function assignCategory(itemId, categoryId) {
    const { error: err } = await supabase.from('takeoff_item').update({ takeoff_category_id: categoryId || null }).eq('id', itemId)
    if (err) { setError(err.message); return }
    await loadReport()
  }

  // Package F (cross-drawing) — the PRIMARY Takeoff Summary now spans
  // the ENTIRE Tender, using each Drawing's LATEST version only. This
  // replaces the old "pick one drawing_version" scope entirely.
  //
  // Scope chain (per the approved architecture):
  //   tender_id -> all drawings -> latest_drawing_version_per_drawing
  //   -> sheets in those versions -> takeoff_item_sheet_breakdown
  //   -> takeoff_item -> takeoff_category
  //
  // takeoff_item_sheet_breakdown itself is NEVER modified — this only
  // adds a filtering step on top of it (WHERE sheet_id IN <sheets
  // belonging to a LATEST version>), exactly as approved.
  async function loadReport() {
    setLoading(true)

    // Step 1: resolve every Drawing in this Tender, and the ONE latest
    // drawing_version_id for each — via the new View, which already
    // guarantees exactly one row per drawing_id (verified: 9 drawings
    // -> 9 rows, tie-broken deterministically by dv.id).
    const { data: drawingsInTender, error: drawingsErr } = await supabase
      .from('drawing').select('id, name').eq('tender_id', tenderId)
    if (drawingsErr) { setError(drawingsErr.message); setLoading(false); return }
    const drawingIds = (drawingsInTender || []).map((d) => d.id)
    const drawingNameById = new Map((drawingsInTender || []).map((d) => [d.id, d.name]))

    if (drawingIds.length === 0) {
      setRows([]); setLoading(false); return
    }

    const { data: latestVersions, error: latestErr } = await supabase
      .from('latest_drawing_version_per_drawing')
      .select('drawing_id, drawing_version_id, label')
      .in('drawing_id', drawingIds)
    if (latestErr) { setError(latestErr.message); setLoading(false); return }

    const latestVersionIds = (latestVersions || []).map((v) => v.drawing_version_id)
    const versionMeta = new Map((latestVersions || []).map((v) => [v.drawing_version_id, { drawingId: v.drawing_id, drawingName: drawingNameById.get(v.drawing_id), label: v.label }]))

    if (latestVersionIds.length === 0) {
      setRows([]); setLoading(false); return
    }

    // Step 2: sheets belonging ONLY to those latest versions.
    const { data: sheetsInLatest, error: sheetsErr } = await supabase
      .from('sheet').select('id, sheet_number, drawing_version_id').in('drawing_version_id', latestVersionIds)
    if (sheetsErr) { setError(sheetsErr.message); setLoading(false); return }
    const sheetMeta = new Map((sheetsInLatest || []).map((s) => [s.id, { sheetNumber: s.sheet_number, ...versionMeta.get(s.drawing_version_id) }]))
    const sheetIds = (sheetsInLatest || []).map((s) => s.id)

    if (sheetIds.length === 0) {
      setRows([]); setLoading(false); return
    }

    // Step 3: takeoff_item_sheet_breakdown, filtered to those sheets
    // only — the existing SSOT View itself is completely untouched.
    const { data, error: err } = await supabase
      .from('takeoff_item_sheet_breakdown')
      .select('takeoff_item_id, sheet_id, aggregated_quantity, unit')
      .in('sheet_id', sheetIds)
    if (err) { setError(err.message); setLoading(false); return }

    const itemIds = [...new Set((data || []).map((r) => r.takeoff_item_id))]
    let itemsById = new Map()
    if (itemIds.length > 0) {
      const { data: items, error: itemsErr } = await supabase
        .from('takeoff_item').select('id, description, takeoff_category_id').in('id', itemIds)
      if (itemsErr) { setError(itemsErr.message); setLoading(false); return }
      itemsById = new Map((items || []).map((it) => [it.id, it]))
    }

    const categoryIds = [...new Set(Array.from(itemsById.values()).map((it) => it.takeoff_category_id).filter(Boolean))]
    let categoriesById = new Map()
    if (categoryIds.length > 0) {
      const { data: cats, error: catsErr } = await supabase
        .from('takeoff_category').select('id, name, code, sort_order').in('id', categoryIds)
      if (catsErr) { setError(catsErr.message); setLoading(false); return }
      categoriesById = new Map((cats || []).map((c) => [c.id, c]))
    }

    const enriched = (data || []).map((r) => {
      const item = itemsById.get(r.takeoff_item_id)
      const category = item?.takeoff_category_id ? categoriesById.get(item.takeoff_category_id) : null
      const meta = sheetMeta.get(r.sheet_id) || {}
      return {
        ...r,
        sheetNumber: meta.sheetNumber || '-',
        drawingName: meta.drawingName || '-',
        revisionLabel: meta.label || '-',
        takeoff_item: item ? { description: item.description, takeoff_category: category || null } : null,
      }
    })
    setRows(enriched)

    if (itemIds.length > 0) {
      // Live status, not the cached out_of_sync checkpoint column -- a
      // measurement can change mid-Draft and this report should reflect
      // that immediately rather than only after whoever is working the
      // Rate Build-Up side happens to attempt the formal Under Review
      // transition (which is what actually refreshes out_of_sync).
      // quantity_source_live_sync_status applies the exact same
      // comparison rule as run_takeoff_sync_gate(), so this view and the
      // formal gate can never disagree.
      const { data: qs } = await supabase
        .from('quantity_source_live_sync_status').select('takeoff_item_id')
        .in('takeoff_item_id', itemIds).eq('live_sync_status', 'needs_recapture')
      setOutOfSyncItemIds(new Set((qs || []).map((r) => r.takeoff_item_id)))
    } else {
      setOutOfSyncItemIds(new Set())
    }
    setLoading(false)
  }

  useEffect(() => { loadReport() }, [tenderId])

  // Package 4 — Review Dashboard data. Same cross-drawing/latest-
  // revision scope as the Summary tab (same drawing/latest_drawing_
  // version_per_drawing resolution), but reads raw active measurement
  // rows directly (WITH review_status/rejection_reason) instead of the
  // pre-aggregated takeoff_item_sheet_breakdown view, for the reason
  // documented on buildReviewSummary() above.
  async function loadReviewData() {
    setLoadingReview(true)
    const { data: drawingsInTender } = await supabase.from('drawing').select('id').eq('tender_id', tenderId)
    const drawingIds = (drawingsInTender || []).map((d) => d.id)
    if (drawingIds.length === 0) { setReviewRows([]); setLoadingReview(false); return }

    const { data: latestVersions } = await supabase
      .from('latest_drawing_version_per_drawing').select('drawing_version_id').in('drawing_id', drawingIds)
    const latestVersionIds = (latestVersions || []).map((v) => v.drawing_version_id)
    if (latestVersionIds.length === 0) { setReviewRows([]); setLoadingReview(false); return }

    const { data: sheetsInLatest } = await supabase.from('sheet').select('id').in('drawing_version_id', latestVersionIds)
    const sheetIds = (sheetsInLatest || []).map((s) => s.id)
    if (sheetIds.length === 0) { setReviewRows([]); setLoadingReview(false); return }

    const { data: rawMeasurements, error: err } = await supabase
      .from('measurement')
      .select('id, calculated_value, polarity, review_status, rejection_reason, takeoff_item_id, sheet_id')
      .in('sheet_id', sheetIds).eq('is_active', true)
    if (err) { setError(err.message); setLoadingReview(false); return }

    const itemIds = [...new Set((rawMeasurements || []).map((m) => m.takeoff_item_id))]
    let itemsById = new Map()
    if (itemIds.length > 0) {
      const { data: items } = await supabase.from('takeoff_item').select('id, description, unit, takeoff_category_id').in('id', itemIds)
      itemsById = new Map((items || []).map((it) => [it.id, it]))
    }
    const categoryIds = [...new Set(Array.from(itemsById.values()).map((it) => it.takeoff_category_id).filter(Boolean))]
    let categoriesById = new Map()
    if (categoryIds.length > 0) {
      const { data: cats } = await supabase.from('takeoff_category').select('id, name, code, sort_order').in('id', categoryIds)
      categoriesById = new Map((cats || []).map((c) => [c.id, c]))
    }

    const enriched = (rawMeasurements || []).map((m) => {
      const item = itemsById.get(m.takeoff_item_id)
      return {
        ...m,
        unit: item?.unit || '',
        itemDescription: item?.description || '(unnamed item)',
        category: item?.takeoff_category_id ? categoriesById.get(item.takeoff_category_id) : null,
      }
    })
    setReviewRows(enriched)
    setLoadingReview(false)
  }

  // Detailed Breakdown tab — load the plain list of all Drawings in
  // this Tender (independent of whether they have measurements), so
  // the user can pick ANY drawing to inspect, including ones the
  // cross-drawing Summary currently shows zero quantity for.
  useEffect(() => {
    let cancelled = false
    async function loadDrawingsList() {
      const { data, error: err } = await supabase.from('drawing').select('id, name').eq('tender_id', tenderId).order('name')
      if (cancelled) return
      if (err) { setError(err.message); return }
      setAllDrawings(data || [])
      if (data && data.length > 0 && !selectedDrawingId) setSelectedDrawingId(data[0].id)
    }
    loadDrawingsList()
    return () => { cancelled = true }
  }, [tenderId])

  // When the selected Drawing changes, load ALL its versions (not just
  // the latest) so the user can pick any one of them.
  useEffect(() => {
    let cancelled = false
    async function loadVersionsForDrawing() {
      if (!selectedDrawingId) { setDrawingVersionsForSelected([]); return }
      const { data, error: err } = await supabase
        .from('drawing_version').select('id, label, issued_at')
        .eq('drawing_id', selectedDrawingId).order('issued_at', { ascending: false })
      if (cancelled) return
      if (err) { setError(err.message); return }
      setDrawingVersionsForSelected(data || [])
      if (data && data.length > 0) setSelectedDetailVersionId(data[0].id) // defaults to the latest, but user can pick any.
      else setSelectedDetailVersionId(null)
    }
    loadVersionsForDrawing()
    return () => { cancelled = true }
  }, [selectedDrawingId])

  // Loads the Detailed Breakdown data for EXACTLY the selected
  // drawing_version_id — a single, explicit version, which may or may
  // not be the drawing's current latest. Uses the SAME
  // takeoff_item_sheet_breakdown SSOT as the Summary; no new
  // calculation engine.
  useEffect(() => {
    let cancelled = false
    async function loadDetail() {
      if (!selectedDetailVersionId) { setDetailRows([]); return }
      setLoadingDetail(true)
      const { data: sheetsInVersion, error: sheetsErr } = await supabase
        .from('sheet').select('id, sheet_number').eq('drawing_version_id', selectedDetailVersionId)
      if (cancelled) return
      if (sheetsErr) { setError(sheetsErr.message); setLoadingDetail(false); return }
      const sheetIdToNumber = new Map((sheetsInVersion || []).map((s) => [s.id, s.sheet_number]))
      const sheetIds = (sheetsInVersion || []).map((s) => s.id)

      if (sheetIds.length === 0) { setDetailRows([]); setLoadingDetail(false); return }

      const { data, error: err } = await supabase
        .from('takeoff_item_sheet_breakdown')
        .select('takeoff_item_id, sheet_id, aggregated_quantity, unit')
        .in('sheet_id', sheetIds)
      if (cancelled) return
      if (err) { setError(err.message); setLoadingDetail(false); return }

      const itemIds = [...new Set((data || []).map((r) => r.takeoff_item_id))]
      let itemsById = new Map()
      if (itemIds.length > 0) {
        const { data: items, error: itemsErr } = await supabase
          .from('takeoff_item').select('id, description, takeoff_category_id').in('id', itemIds)
        if (cancelled) return
        if (itemsErr) { setError(itemsErr.message); setLoadingDetail(false); return }
        itemsById = new Map((items || []).map((it) => [it.id, it]))
      }

      const categoryIds = [...new Set(Array.from(itemsById.values()).map((it) => it.takeoff_category_id).filter(Boolean))]
      let categoriesById = new Map()
      if (categoryIds.length > 0) {
        const { data: cats, error: catsErr } = await supabase
          .from('takeoff_category').select('id, name, code, sort_order').in('id', categoryIds)
        if (cancelled) return
        if (catsErr) { setError(catsErr.message); setLoadingDetail(false); return }
        categoriesById = new Map((cats || []).map((c) => [c.id, c]))
      }

      const drawingMeta = allDrawings.find((d) => d.id === selectedDrawingId)
      const versionMeta = drawingVersionsForSelected.find((v) => v.id === selectedDetailVersionId)

      const enriched = (data || []).map((r) => {
        const item = itemsById.get(r.takeoff_item_id)
        const category = item?.takeoff_category_id ? categoriesById.get(item.takeoff_category_id) : null
        return {
          ...r,
          sheetNumber: sheetIdToNumber.get(r.sheet_id) || '-',
          drawingName: drawingMeta?.name || '-',
          revisionLabel: versionMeta?.label || '-',
          takeoff_item: item ? { description: item.description, takeoff_category: category || null } : null,
        }
      })
      setDetailRows(enriched)
      setLoadingDetail(false)
    }
    loadDetail()
    return () => { cancelled = true }
  }, [selectedDetailVersionId])

  async function toggleSheet(itemId, sheetId) {
    const key = `${itemId}::${sheetId}`
    setExpandedSheets((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })
    if (measurementsByKey.has(key)) return
    setLoadingMeasurementKey(key)
    const { data, error: err } = await supabase
      .from('measurement')
      .select('id, measurement_type, calculated_value, polarity, source_type, created_at, geometry_data')
      .eq('sheet_id', sheetId).eq('takeoff_item_id', itemId).eq('is_active', true)
      .order('created_at')
    setLoadingMeasurementKey(null)
    if (err) { setError(err.message); return }
    setMeasurementsByKey((prev) => new Map(prev).set(key, data || []))
  }

  function toggleCategory(id) { setExpandedCategories((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n }) }
  function toggleItem(id) { setExpandedItems((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n }) }

  const allCategories = buildCategoryTree(rows)

  const categories = searchQuery.trim()
    ? filterCategoryTree(allCategories, searchQuery.trim().toLowerCase())
    : allCategories

  const quantitySummary = buildQuantitySummary(allCategories) // replaces the invalid mixed-unit "Grand Total" — grouped strictly by unit.
  const takeoffSummaryRows = buildTakeoffSummaryRows(allCategories) // PRIMARY view: Category x Unit rows, always visible.
  const totalItems = allCategories.reduce((sum, c) => sum + c.items.length, 0)
  const totalSheets = new Set(rows.map((r) => r.sheet_id)).size
  const totalMeasurements = Array.from(measurementsByKey.values()).reduce((sum, arr) => sum + arr.length, 0)

  function expandAll() {
    setExpandedCategories(new Set(allCategories.map((c) => c.id)))
    setExpandedItems(new Set(allCategories.flatMap((c) => c.items.map((i) => i.id))))
  }
  function collapseAll() {
    setExpandedCategories(new Set())
    setExpandedItems(new Set())
    setExpandedSheets(new Set())
  }

  if (loading) return <div style={{ padding: 40, color: colors.textMuted, fontSize: 13 }}>Loading report...</div>
  if (error) return <div style={{ padding: 40, color: colors.danger, fontSize: 13 }}>{error}</div>

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px 60px' }}>
      <EstimationBreadcrumb to={`/estimation/tender/${tenderId}/revision/${revisionId}/takeoff`} label={'\u2190 Back to Takeoff'} />

      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>
          Construction Takeoff Report
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color: colors.textPrimary, marginTop: 2, marginBottom: 12 }}>
          Current Project Takeoff
        </div>
        <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <HeaderFact icon="📁" label="Tender" value={tenderName || '-'} />
          <HeaderFact icon="📐" label="Drawings" value={`${new Set(rows.map((r) => r.drawingName)).size} (latest revision each)`} />
          <HeaderFact icon="📅" label="Report Date" value={new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} />
          <HeaderFact icon="👤" label="Prepared By" value={preparedBy || '-'} />
          <div style={{ flex: 1 }} />
          <button
            onClick={() => exportTakeoffReportPdf({ categories: allCategories, summaryRows: takeoffSummaryRows, tenderName, preparedBy })}
            style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer', height: 'fit-content', whiteSpace: 'nowrap' }}
          >
            🖨 Export PDF
          </button>
          <button
            disabled={exportingExcel}
            onClick={async () => {
              setExportingExcel(true)
              try {
                const wb = await exportTakeoffReportExcel({ categories: allCategories, summaryRows: takeoffSummaryRows, tenderName })
                const buffer = await wb.xlsx.writeBuffer()
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `Takeoff-Report-${new Date().toISOString().slice(0, 10)}.xlsx`
                a.click()
                URL.revokeObjectURL(url)
              } catch (e) {
                setError(e.message || 'Excel export failed.')
              } finally {
                setExportingExcel(false)
              }
            }}
            style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 12px', cursor: exportingExcel ? 'default' : 'pointer', height: 'fit-content', whiteSpace: 'nowrap', opacity: exportingExcel ? 0.6 : 1 }}
          >
            📊 {exportingExcel ? 'Exporting...' : 'Export Excel'}
          </button>
          <button
            onClick={() => setShowCategoryManagement(true)}
            style={{ fontSize: 12, fontWeight: 600, color: colors.primary, background: '#fff', border: `1px solid ${colors.primary}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer', height: 'fit-content', whiteSpace: 'nowrap' }}
          >
            ⚙ Manage Categories
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        <MetricCard color="#2563EB" value={allCategories.length} label="Categories" />
        <MetricCard color="#16A34A" value={totalItems} label="Takeoff Items" />
        <MetricCard color="#EA580C" value={totalSheets} label="Sheets" />
        <MetricCard color="#7C3AED" value={totalMeasurements || '—'} label="Measurements" />
      </div>

      {activeTab === 'summary' && (
      <>
      {/* Quantity Summary — replaces the old invalid mixed-unit Grand
          Total. Grouped strictly by unit; never combines m/m2/m3/unit
          into one number. */}
      {quantitySummary.length > 0 && (
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, background: '#F8FAFC' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Quantity Summary
          </div>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            {quantitySummary.map(({ unit, total }) => (
              <div key={unit} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                  {formatQuantity(total, '').trim()}
                </span>
                <span style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>{unit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* TAKEOFF SUMMARY — the PRIMARY, quantity-first view. One row
          per (Category, Unit). Always visible, no expand needed. This
          is what the user sees first: "what quantities have I
          measured?" — before any drill-down into items/sheets/
          measurements. */}
      {/* ============================================================ */}
      {takeoffSummaryRows.length > 0 && (
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 20, background: '#fff' }}>
          <div style={{ padding: '10px 14px', background: '#0F172A', color: '#fff', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Takeoff Summary
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.textPrimary}` }}>
                <th style={{ textAlign: 'left', padding: '8px 14px', fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Category</th>
                <th style={{ textAlign: 'center', padding: '8px 14px', fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, width: 90 }}>Unit</th>
                <th style={{ textAlign: 'right', padding: '8px 14px', fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, width: 160 }}>Total Quantity</th>
              </tr>
            </thead>
            <tbody>
              {takeoffSummaryRows.map((row, idx) => (
                <tr key={`${row.categoryId}::${row.unit}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                    {/* Category name only shown once per group, on its first unit row — subsequent unit rows for the same multi-unit category are indented and unlabeled, avoiding repetition. */}
                    {idx === 0 || takeoffSummaryRows[idx - 1].categoryId !== row.categoryId
                      ? <>
                          <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#fff', background: CATEGORY_COLORS[(row.categoryIndex - 1) % CATEGORY_COLORS.length], borderRadius: 4, padding: '2px 6px', marginRight: 8 }}>
                            {String(row.categoryIndex).padStart(2, '0')}
                          </span>
                          {row.categoryName.toUpperCase()}
                        </>
                      : <span style={{ paddingLeft: 34, color: colors.textMuted, fontWeight: 400, fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: '9px 14px', textAlign: 'center', fontSize: 12, color: colors.textMuted }}>{row.unit}</td>
                  <td style={{ padding: '9px 14px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                    {formatQuantity(row.total, row.unit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {/* ============================================================ */}
      {/* TABS — Summary is always cross-drawing/latest-only. Detail is
          a fully separate tab, scoped to ONE explicitly chosen
          Drawing + Version (any version, not just the latest) — this
          is the "detailed audit" view, independent of the Summary's
          scope. */}
      {/* ============================================================ */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `2px solid ${colors.border}` }}>
        <button
          onClick={() => setActiveTab('summary')}
          style={{
            fontSize: 13, fontWeight: 700, padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            color: activeTab === 'summary' ? colors.primary : colors.textMuted,
            borderBottom: activeTab === 'summary' ? `2px solid ${colors.primary}` : '2px solid transparent',
            marginBottom: -2,
          }}
        >
          Takeoff Summary
        </button>
        <button
          onClick={() => setActiveTab('detail')}
          style={{
            fontSize: 13, fontWeight: 700, padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            color: activeTab === 'detail' ? colors.primary : colors.textMuted,
            borderBottom: activeTab === 'detail' ? `2px solid ${colors.primary}` : '2px solid transparent',
            marginBottom: -2,
          }}
        >
          Detailed Breakdown
        </button>
        <button
          onClick={() => { setActiveTab('review'); if (reviewRows.length === 0) loadReviewData() }}
          style={{
            fontSize: 13, fontWeight: 700, padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            color: activeTab === 'review' ? colors.primary : colors.textMuted,
            borderBottom: activeTab === 'review' ? `2px solid ${colors.primary}` : '2px solid transparent',
            marginBottom: -2,
          }}
        >
          Review Dashboard
        </button>
      </div>

      {activeTab === 'detail' && (
      <>
      {/* Drawing + Version filter — independent of the Summary's
          latest-only scope. Defaults to the latest version of the
          first drawing, but the user can pick ANY drawing and ANY of
          its versions (including superseded ones) to audit. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: colors.textMuted }}>Drawing</div>
          <select
            value={selectedDrawingId || ''}
            onChange={(e) => setSelectedDrawingId(e.target.value)}
            style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, border: `1px solid ${colors.border}`, marginTop: 1 }}
          >
            {allDrawings.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, color: colors.textMuted }}>Revision</div>
          <select
            value={selectedDetailVersionId || ''}
            onChange={(e) => setSelectedDetailVersionId(e.target.value)}
            style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, border: `1px solid ${colors.border}`, marginTop: 1 }}
          >
            {drawingVersionsForSelected.map((v, idx) => (
              <option key={v.id} value={v.id}>{v.label}{idx === 0 ? ' (latest)' : ''}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted, fontSize: 13 }}>🔍</span>
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search items, sheets, measurements..."
            style={{ width: '100%', paddingLeft: 30, fontSize: 13 }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.textSecondary, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Compact View
          <input type="checkbox" checked={compactView} onChange={(e) => setCompactView(e.target.checked)} />
        </label>
        <button onClick={expandAll} style={{ fontSize: 12, color: colors.textSecondary, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Expand All</button>
        <button onClick={collapseAll} style={{ fontSize: 12, color: colors.textSecondary, background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Collapse All</button>
      </div>

      {loadingDetail ? (
        <div style={{ color: colors.textMuted, fontSize: 13, padding: '30px 0', textAlign: 'center' }}>Loading...</div>
      ) : (() => {
        const detailAllCategories = buildCategoryTree(detailRows)
        const detailCategories = searchQuery.trim()
          ? filterCategoryTree(detailAllCategories, searchQuery.trim().toLowerCase())
          : detailAllCategories
        const detailTotalItems = detailAllCategories.reduce((sum, c) => sum + c.items.length, 0)
        const detailTotalSheets = new Set(detailRows.map((r) => r.sheet_id)).size

        return (
      <>
      {detailCategories.length > 0 && (
        <div style={{ display: 'flex', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ flex: 1 }}>Code / Description</div>
          <div style={{ width: 90, textAlign: 'center' }}>Unit</div>
          <div style={{ width: 150, textAlign: 'right' }}>Quantity</div>
          <div style={{ width: 24 }} />
        </div>
      )}

      {detailCategories.length === 0 ? (
        <div style={{ color: colors.textMuted, fontSize: 13, padding: '30px 0', textAlign: 'center' }}>
          {searchQuery ? 'No results match your search.' : 'No takeoff quantities found for this Drawing/Revision.'}
        </div>
      ) : (
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
          {detailCategories.map((cat, catIdx) => (
            <CategorySection
              key={cat.id}
              index={catIdx + 1}
              color={CATEGORY_COLORS[catIdx % CATEGORY_COLORS.length]}
              category={cat}
              expanded={expandedCategories.has(cat.id)}
              onToggle={() => toggleCategory(cat.id)}
              expandedItems={expandedItems}
              onToggleItem={toggleItem}
              outOfSyncItemIds={outOfSyncItemIds}
              expandedSheets={expandedSheets}
              onToggleSheet={toggleSheet}
              measurementsByKey={measurementsByKey}
              loadingMeasurementKey={loadingMeasurementKey}
              compactView={compactView}
              availableCategories={availableCategories}
              onAssignCategory={assignCategory}
              onViewDrawing={openMeasurementPreview}
            />
          ))}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#F8FAFC', borderTop: `2px solid ${colors.textPrimary}` }}>
            <div style={{ fontSize: 11, color: colors.textMuted }}>
              {detailAllCategories.length} Categories · {detailTotalItems} Takeoff Items · {detailTotalSheets} Sheet{detailTotalSheets !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}
      </>
        )
      })()}
      </>
      )}

      {/* ============================================================ */}
      {/* REVIEW DASHBOARD — Package 4. Reads raw active measurements
          directly (via loadReviewData), NOT the pre-aggregated
          takeoff_item_sheet_breakdown. Approve/Reject scope is
          (Takeoff Item x Drawing Version), matching the approved RPCs
          exactly. Every action reloads from the database — no local
          review state engine. */}
      {/* ============================================================ */}
      {activeTab === 'review' && (
        <div>
          {loadingReview ? (
            <div style={{ color: colors.textMuted, fontSize: 13, padding: '30px 0', textAlign: 'center' }}>Loading review data...</div>
          ) : reviewRows.length === 0 ? (
            <div style={{ color: colors.textMuted, fontSize: 13, padding: '30px 0', textAlign: 'center' }}>No measurements found for this revision.</div>
          ) : (() => {
            const reviewSummary = buildReviewSummary(reviewRows)
            // Group raw rows by (takeoff_item_id, sheet's drawing_version)
            // is not directly available here (sheet_id only) — the
            // Approve/Reject RPCs need a drawing_version_id, so resolve
            // it once via the sheetIds already implicitly scoped to
            // latest versions (loadReviewData already filtered to
            // latest-per-drawing sheets only, so ANY sheet_id present
            // here already belongs to a valid target version).
            return (
              <div>
                <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${colors.textPrimary}` }}>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase' }}>Category</th>
                        <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase' }}>Unit</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase' }}>Current</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#065F46', textTransform: 'uppercase' }}>Accepted</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#92400E', textTransform: 'uppercase' }}>Pending</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#991B1B', textTransform: 'uppercase' }}>Rejected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewSummary.map((cat) => cat.unitRows.map((u, idx) => (
                        <tr key={`${cat.id}::${u.unit}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: idx === 0 ? 700 : 400, color: colors.textPrimary }}>{idx === 0 ? cat.name.toUpperCase() : ''}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, color: colors.textMuted }}>{u.unit}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, fontWeight: 700 }}>{formatNumber(u.current)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: '#065F46' }}>{formatNumber(u.accepted)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: '#92400E' }}>{formatNumber(u.pending)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: '#991B1B' }}>{formatNumber(u.rejected)}</td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>

                {/* Pending items — grouped by (takeoff_item_id, sheet_id)
                    so Approve/Reject can target the exact
                    (item, drawing_version) scope the RPCs expect. */}
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, marginBottom: 8, textTransform: 'uppercase' }}>Pending Review</div>
                  {(() => {
                    const pendingByKey = new Map()
                    for (const row of reviewRows) {
                      if (row.review_status !== 'pending') continue
                      const key = `${row.takeoff_item_id}::${row.sheet_id}`
                      if (!pendingByKey.has(key)) pendingByKey.set(key, { takeoffItemId: row.takeoff_item_id, sheetId: row.sheet_id, description: row.itemDescription, unit: row.unit, count: 0 })
                      pendingByKey.get(key).count++
                    }
                    const pendingGroups = Array.from(pendingByKey.values())
                    if (pendingGroups.length === 0) return <div style={{ fontSize: 12, color: colors.textMuted }}>Nothing pending.</div>
                    return pendingGroups.map((g) => (
                      <div key={`${g.takeoffItemId}::${g.sheetId}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, marginBottom: 6 }}>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>{g.description}</span>
                        <span style={{ fontSize: 11, color: colors.textMuted }}>{g.count} measurement{g.count !== 1 ? 's' : ''}</span>
                        <button
                          onClick={async () => {
                            const { data: sheet } = await supabase.from('sheet').select('drawing_version_id').eq('id', g.sheetId).maybeSingle()
                            const { error } = await supabase.rpc('accept_pending_review', { p_takeoff_item_id: g.takeoffItemId, p_drawing_version_id: sheet.drawing_version_id })
                            if (error) setError(error.message)
                            await loadReviewData()
                          }}
                          style={{ fontSize: 11, fontWeight: 600, color: '#065F46', background: '#D1FAE5', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => setReviewSelectedItem({ takeoffItemId: g.takeoffItemId, sheetId: g.sheetId })}
                          style={{ fontSize: 11, fontWeight: 600, color: '#991B1B', background: '#FEE2E2', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}
                        >
                          Reject
                        </button>
                      </div>
                    ))
                  })()}
                </div>

                {/* Rejected items — shown for traceability with their reason. */}
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, marginBottom: 8, textTransform: 'uppercase' }}>Rejected</div>
                  {reviewRows.filter((r) => r.review_status === 'rejected').length === 0 ? (
                    <div style={{ fontSize: 12, color: colors.textMuted }}>None rejected.</div>
                  ) : (
                    (() => {
                      const seen = new Set()
                      return reviewRows.filter((r) => r.review_status === 'rejected').map((r) => {
                        const key = `${r.takeoff_item_id}::${r.sheet_id}`
                        if (seen.has(key)) return null
                        seen.add(key)
                        return (
                          <div key={key} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, marginBottom: 6 }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{r.itemDescription}</div>
                            <div style={{ fontSize: 11, color: '#991B1B', marginTop: 2 }}>{r.rejection_reason}</div>
                          </div>
                        )
                      })
                    })()
                  )}
                </div>

                {/* Reject reason prompt — appears inline once a Reject is clicked. */}
                {reviewSelectedItem && (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 150 }}>
                    <div style={{ background: '#fff', borderRadius: 8, padding: 20, width: 400 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Rejection Reason</div>
                      <textarea
                        value={rejectReasonDraft}
                        onChange={(e) => setRejectReasonDraft(e.target.value)}
                        style={{ width: '100%', minHeight: 80, fontSize: 12, padding: 8, borderRadius: 6, border: `1px solid ${colors.border}` }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                        <button onClick={() => { setReviewSelectedItem(null); setRejectReasonDraft('') }} style={{ fontSize: 12, color: colors.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
                        <button
                          onClick={async () => {
                            const { data: sheet } = await supabase.from('sheet').select('drawing_version_id').eq('id', reviewSelectedItem.sheetId).maybeSingle()
                            const { error } = await supabase.rpc('reject_pending_review', { p_takeoff_item_id: reviewSelectedItem.takeoffItemId, p_drawing_version_id: sheet.drawing_version_id, p_reason: rejectReasonDraft })
                            if (error) setError(error.message)
                            setReviewSelectedItem(null); setRejectReasonDraft('')
                            await loadReviewData()
                          }}
                          style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: '#991B1B', border: 'none', borderRadius: 4, padding: '6px 12px', cursor: 'pointer' }}
                        >
                          Confirm Reject
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
        </div>
      )}

      {showCategoryManagement && (
        <CategoryManagement
          tenderId={tenderId}
          onClose={async () => {
            setShowCategoryManagement(false)
            const { data: cats } = await supabase.from('takeoff_category').select('id, name, code').eq('tender_id', tenderId).eq('is_active', true).order('sort_order')
            setAvailableCategories(cats || [])
            loadReport()
          }}
        />
      )}

      {previewMeasurement && (
        <MeasurementPreview
          measurement={previewMeasurement}
          sheetId={previewSheetId}
          drawingVersionId={previewDrawingVersionId}
          onClose={() => { setPreviewMeasurement(null); setPreviewSheetId(null); setPreviewDrawingVersionId(null) }}
        />
      )}
    </div>
  )
}

function HeaderFact({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 10, color: colors.textMuted }}>{label}</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>{value}</div>
      </div>
    </div>
  )
}

function MetricCard({ color, value, label, isSum }) {
  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, background: '#fff' }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}1A`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
        {isSum ? 'Σ' : '#'}
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 10, color: colors.textMuted }}>{label}</div>
      </div>
    </div>
  )
}

// Unit-aware aggregation — the core fix. Every level (Category, Item)
// now aggregates into a Map<unit, quantity> instead of a single
// number, so quantities in different units are NEVER summed together
// (m² + m³ + m is mathematically meaningless and must never appear as
// one number). Sheet-level quantities are already single-unit by
// construction (one takeoff_item_sheet_breakdown row = one unit), so
// no change is needed there beyond carrying the unit alongside.
export function buildCategoryTree(rows) {
  const categoryMap = new Map()
  for (const row of rows) {
    const cat = row.takeoff_item?.takeoff_category
    const catId = cat?.id || 'uncategorized'
    const catName = cat?.name || 'Uncategorized'
    const catCode = cat?.code || null
    const catOrder = cat?.sort_order ?? Number.MAX_SAFE_INTEGER
    if (!categoryMap.has(catId)) categoryMap.set(catId, { id: catId, name: catName, code: catCode, sortOrder: catOrder, items: new Map(), totalsByUnit: new Map() })
    const category = categoryMap.get(catId)

    const itemId = row.takeoff_item_id
    const unit = row.unit
    if (!category.items.has(itemId)) {
      category.items.set(itemId, { id: itemId, description: row.takeoff_item?.description || '(unnamed item)', unit, sheets: [], total: 0 })
    }
    const item = category.items.get(itemId)
    const qty = Number(row.aggregated_quantity) || 0
    item.sheets.push({ sheetId: row.sheet_id, sheetNumber: row.sheetNumber || '-', drawingName: row.drawingName || '-', revisionLabel: row.revisionLabel || '-', quantity: qty })
    item.total += qty // safe: every sheet row under the SAME item always shares the SAME unit (one takeoff_item has exactly one unit).
    category.totalsByUnit.set(unit, (category.totalsByUnit.get(unit) || 0) + qty) // never summed across units.
  }
  return Array.from(categoryMap.values())
    .map((cat) => ({ ...cat, items: Array.from(cat.items.values()), totalsByUnit: cat.totalsByUnit }))
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

// Report-wide Quantity Summary — grouped strictly by unit, across ALL
// categories/items. This replaces the old single mixed-unit "Grand
// Total" entirely. Only units that actually exist in this revision
// are shown.
export function buildQuantitySummary(categories) {
  const byUnit = new Map()
  for (const cat of categories) {
    for (const [unit, qty] of cat.totalsByUnit) {
      byUnit.set(unit, (byUnit.get(unit) || 0) + qty)
    }
  }
  return Array.from(byUnit.entries())
    .map(([unit, total]) => ({ unit, total }))
    .sort((a, b) => a.unit.localeCompare(b.unit))
}

// Package 4 — Review Dashboard aggregation. Deliberately SEPARATE from
// buildCategoryTree(): that function aggregates takeoff_item_sheet_
// breakdown (itself pre-aggregated per sheet, with no review_status
// dimension at all — a Sheet's rows there are already summed across
// every measurement on it, mixing review states). Forcing Review onto
// that shape would silently misrepresent Current/Accepted/Pending/
// Rejected. This function instead aggregates raw active measurement
// rows directly, grouped by (category, unit, review_status) — the
// SAME simple SUM operation used everywhere else in this file, just
// with one additional grouping key. No new calculation semantics, no
// BOQ/quantity_source involvement.
export function buildReviewSummary(measurementRows) {
  // measurementRows: [{ takeoff_item_id, unit, calculated_value, polarity,
  //   review_status, category: { id, name, code, sort_order } | null }]
  const byCategory = new Map()
  for (const row of measurementRows) {
    const cat = row.category
    const catId = cat?.id || 'uncategorized'
    const catName = cat?.name || 'Uncategorized'
    const catOrder = cat?.sort_order ?? Number.MAX_SAFE_INTEGER
    if (!byCategory.has(catId)) byCategory.set(catId, { id: catId, name: catName, sortOrder: catOrder, byUnit: new Map() })
    const category = byCategory.get(catId)

    const unit = row.unit
    if (!category.byUnit.has(unit)) {
      category.byUnit.set(unit, { unit, current: 0, accepted: 0, pending: 0, rejected: 0, unreviewed: 0 })
    }
    const bucket = category.byUnit.get(unit)
    const signed = Number(row.calculated_value) * (row.polarity === 'negative' ? -1 : 1)
    const status = row.review_status || 'unreviewed'
    // Current = the authoritative Takeoff quantity: every active measurement except Rejected ones.
    if (status !== 'rejected') bucket.current += signed
    if (status === 'accepted') bucket.accepted += signed
    else if (status === 'pending') bucket.pending += signed
    else if (status === 'rejected') bucket.rejected += signed
    else bucket.unreviewed += signed
  }
  return Array.from(byCategory.values())
    .map((cat) => ({ ...cat, unitRows: Array.from(cat.byUnit.values()) }))
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

// TAKEOFF SUMMARY — the primary, quantity-first view. One row per
// (Category, Unit) pair, flattened directly from the SAME
// category.totalsByUnit Map buildCategoryTree already computes — this
// is a pure re-shaping/flattening step, NOT a second aggregation
// engine. No measurement is re-summed here; every number already
// exists in `categories` before this function runs. Ordered by the
// category's own sort_order (never alphabetical), matching the
// report's numbering.
export function buildTakeoffSummaryRows(categories) {
  const rows = []
  categories.forEach((cat, catIdx) => {
    for (const [unit, qty] of cat.totalsByUnit) {
      rows.push({ categoryId: cat.id, categoryIndex: catIdx + 1, categoryName: cat.name, unit, total: qty })
    }
  })
  return rows
}

export function filterCategoryTree(categories, query) {
  return categories
    .map((cat) => {
      const catMatches = cat.name.toLowerCase().includes(query)
      const filteredItems = cat.items
        .map((item) => {
          const itemMatches = item.description.toLowerCase().includes(query)
          const filteredSheets = item.sheets.filter((s) => s.sheetNumber.toLowerCase().includes(query) || s.drawingName.toLowerCase().includes(query))
          if (catMatches || itemMatches || filteredSheets.length > 0) {
            return { ...item, sheets: catMatches || itemMatches ? item.sheets : filteredSheets }
          }
          return null
        })
        .filter(Boolean)
      if (catMatches || filteredItems.length > 0) return { ...cat, items: catMatches ? cat.items : filteredItems }
      return null
    })
    .filter(Boolean)
}

function CategorySection({ index, color, category, expanded, onToggle, expandedItems, onToggleItem, outOfSyncItemIds, expandedSheets, onToggleSheet, measurementsByKey, loadingMeasurementKey, compactView, availableCategories, onAssignCategory, onViewDrawing }) {
  const codeLabel = String(index).padStart(2, '0')
  const unitTotals = Array.from(category.totalsByUnit.entries()) // [[unit, qty], ...] — never combined into one number.
  const isSingleUnit = unitTotals.length === 1
  return (
    <div style={{ borderBottom: `2px solid ${colors.border}` }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 12px',
          border: 'none', borderLeft: `5px solid ${color}`, background: '#F8FAFC', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: color, borderRadius: 4, padding: '3px 8px', minWidth: 24, textAlign: 'center' }}>
          {codeLabel}
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
          {category.name}
        </span>
        <span style={{ width: 90, textAlign: 'center' }} />
        <div style={{ width: 140, textAlign: 'right' }}>
          {isSingleUnit ? (
            <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
              {formatQuantity(unitTotals[0][1], unitTotals[0][0])}
            </span>
          ) : (
            unitTotals.map(([unit, qty]) => (
              <div key={unit} style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                {formatQuantity(qty, unit)}
              </div>
            ))
          )}
        </div>
        <span style={{ width: 24, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>{expanded ? '▴' : '▾'}</span>
      </button>

      {expanded && (
        <div>
          {category.items.map((item, itemIdx) => (
            <ItemRow
              key={item.id}
              codePrefix={`${codeLabel}.${String(itemIdx + 1).padStart(2, '0')}`}
              item={item}
              expanded={expandedItems.has(item.id)}
              onToggle={() => onToggleItem(item.id)}
              isOutOfSync={outOfSyncItemIds.has(item.id)}
              expandedSheets={expandedSheets}
              onToggleSheet={onToggleSheet}
              measurementsByKey={measurementsByKey}
              loadingMeasurementKey={loadingMeasurementKey}
              compactView={compactView}
              availableCategories={availableCategories}
              onAssignCategory={onAssignCategory}
              currentCategoryId={category.id === 'uncategorized' ? '' : category.id}
              onViewDrawing={onViewDrawing}
            />
          ))}
          <div style={{ padding: '8px 12px 8px 44px', background: '#FAFAFA', borderTop: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: unitTotals.length > 1 ? 4 : 0 }}>
              Category Total
            </div>
            {unitTotals.map(([unit, qty]) => (
              <div key={unit} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: colors.textPrimary }}>
                {unitTotals.length > 1 && <span style={{ color: colors.textMuted, fontWeight: 500 }}>{unit}</span>}
                <span style={{ fontVariantNumeric: 'tabular-nums', marginLeft: 'auto' }}>{formatQuantity(qty, unit)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ItemRow({ codePrefix, item, expanded, onToggle, isOutOfSync, expandedSheets, onToggleSheet, measurementsByKey, loadingMeasurementKey, compactView, availableCategories, onAssignCategory, currentCategoryId, onViewDrawing }) {
  const rowPadding = compactView ? '6px 12px 6px 44px' : '10px 12px 10px 44px'
  return (
    <div style={{ borderTop: `1px solid ${colors.border}` }}>
      <button
        onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: rowPadding, border: 'none', background: '#fff', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, background: '#F1F5F9', borderRadius: 4, padding: '2px 6px', minWidth: 34, textAlign: 'center' }}>
          {codePrefix}
        </span>
        <span style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>
            {item.description}
            {isOutOfSync && (
              <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 600, color: colors.warningText, background: colors.warningBg, padding: '1px 5px', borderRadius: 3 }}>
                OUT OF SYNC
              </span>
            )}
          </span>
          {!compactView && (
            <span style={{ display: 'block', fontSize: 11, color: colors.textMuted, marginTop: 1 }}>
              {item.unit} · {item.sheets.length} sheet{item.sheets.length !== 1 ? 's' : ''}
            </span>
          )}
        </span>
        <span style={{ width: 150, textAlign: 'right', fontSize: 15, fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
          {formatQuantity(item.total, item.unit)}
        </span>
        <span style={{ width: 24, textAlign: 'center', color: colors.textMuted, fontSize: 12 }}>{expanded ? '▴' : '▾'}</span>
      </button>

      {expanded && item.sheets.map((s) => {
        const key = `${item.id}::${s.sheetId}`
        const sheetExpanded = expandedSheets.has(key)
        const measurements = measurementsByKey.get(key)
        return (
          <div key={s.sheetId} style={{ borderTop: `1px solid ${colors.border}`, background: '#FAFBFC' }}>
            <button
              onClick={() => onToggleSheet(item.id, s.sheetId)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px 6px 68px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <span style={{ flex: 1 }}>
                <span style={{ fontSize: 11, color: colors.textSecondary, fontWeight: 500 }}>{s.drawingName} — Rev {s.revisionLabel} — Sheet {s.sheetNumber}</span>
                {!compactView && (
                  <span style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginTop: 1 }}>
                    {measurements ? `${measurements.length} measurement${measurements.length !== 1 ? 's' : ''}` : ''}
                  </span>
                )}
              </span>
              <span style={{ width: 150, textAlign: 'right', fontSize: 12, fontWeight: 600, color: colors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                {formatQuantity(s.quantity, item.unit)}
              </span>
              <span style={{ width: 24, textAlign: 'center', color: colors.textMuted, fontSize: 10 }}>{sheetExpanded ? '▴' : '▾'}</span>
            </button>

            {sheetExpanded && (
              <div style={{ padding: '0 12px 10px 68px' }}>
                {loadingMeasurementKey === key ? (
                  <div style={{ fontSize: 11, color: colors.textMuted, padding: '4px 0' }}>Loading measurements...</div>
                ) : measurements && measurements.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 4 }}>
                    <thead>
                      <tr style={{ color: colors.textMuted, textTransform: 'uppercase', fontSize: 9, letterSpacing: 0.3 }}>
                        <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: 600 }}>Measurement</th>
                        <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: 600 }}>Type</th>
                        <th style={{ textAlign: 'center', padding: '3px 6px', fontWeight: 600 }}>Polarity</th>
                        <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 600 }}>Quantity</th>
                        <th style={{ padding: '3px 6px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {measurements.map((m, idx) => (
                        <tr key={m.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                          <td style={{ padding: '4px 6px', color: colors.textPrimary }}>Measurement {String(idx + 1).padStart(3, '0')}</td>
                          <td style={{ padding: '4px 6px', color: colors.textSecondary, textTransform: 'capitalize' }}>{m.measurement_type}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'center', color: m.polarity === 'negative' ? colors.danger : colors.accentGreen, fontWeight: 700 }}>
                            {m.polarity === 'negative' ? '−' : '+'}
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                            {(m.polarity === 'negative' ? '-' : '+') + formatNumber(Math.abs(Number(m.calculated_value)))} {item.unit}
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                            {onViewDrawing && m.geometry_data && (
                              <button onClick={() => onViewDrawing(m, s.sheetId)} style={{ fontSize: 10, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                                View Drawing
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: `2px solid ${colors.border}` }}>
                        <td colSpan={3} style={{ padding: '5px 6px', fontWeight: 700, color: colors.textPrimary }}>Sheet Total ({s.sheetNumber})</td>
                        <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                          {formatQuantity(s.quantity, item.unit)}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <div style={{ fontSize: 11, color: colors.textMuted, padding: '4px 0' }}>No measurements found.</div>
                )}
              </div>
            )}
          </div>
        )
      })}

      {expanded && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px 6px 68px', fontSize: 11, fontWeight: 700, color: colors.textPrimary, background: '#FAFBFC', borderTop: `1px solid ${colors.border}` }}>
            <span>Item Total ({item.description.toUpperCase()})</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatQuantity(item.total, item.unit)}</span>
          </div>
          {onAssignCategory && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 8px 68px', fontSize: 11, color: colors.textMuted, background: '#FAFBFC' }}>
              <span>Category:</span>
              <select
                value={currentCategoryId || ''}
                onChange={(e) => onAssignCategory(item.id, e.target.value)}
                style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, border: `1px solid ${colors.border}` }}
              >
                <option value="">Uncategorized</option>
                {availableCategories?.map((c) => (
                  <option key={c.id} value={c.id}>{c.code ? `${c.code} — ` : ''}{c.name}</option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
    </div>

  )
}

function formatQuantity(value, unit) {
  const isCountUnit = /^(ea|unit|no|nos)$/i.test((unit || '').trim())
  const formatted = isCountUnit ? Math.round(value).toLocaleString('en-US') : formatNumber(value)
  return `${formatted} ${unit || ''}`.trim()
}
